'use client';

import {
	UserSettings,
	isDev,
} from '../../environmentVariables/EnvironmentVariables';
import SettingsSwitch from './components/SettingsSwitch';
import Button from 'src/components/primitives/Button/Button';
import { Settings } from 'src/components/Settings/SettingsForm';
import Divider from 'src/components/primitives/Divider/Divider';
import useModal from 'src/components/Modals/hooks/useModal';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import { BaseRadioItemProps } from 'src/components/primitives/RadioGroup/types';
import { Download, Trash } from '@velocity-exchange/icons';
import { useDeleteAccount } from 'src/hooks/useDeleteAccount';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';

interface Props {
	localSettings: Settings;
	toggleSetting: (key: keyof UserSettings) => void;
	updateSettings: (settings: Settings) => void;
}

const EXPLORER_OPTIONS: BaseRadioItemProps<UserSettings['explorer']>[] = [
	{
		id: 'solana',
		value: 'solana',
		label: 'Solana Explorer',
	},
	{
		id: 'solscan',
		value: 'solscan',
		label: 'Solscan',
	},
	{
		id: 'xray',
		value: 'xray',
		label: 'XRAY',
	},
	{
		id: 'solanafm',
		value: 'solanafm',
		label: 'Solana FM',
	},
];

const SWAP_CLIENT_OPTIONS: BaseRadioItemProps<
	UserSettings['swapClientType']
>[] = [
	{
		id: 'titan',
		value: 'titan',
		label: 'Titan',
	},
	{
		id: 'jupiter',
		value: 'jupiter',
		label: 'Jupiter',
	},
];

const OtherSettings = ({
	localSettings,
	toggleSetting,
	updateSettings,
}: Props) => {
	const connected = useWalletIsConnected();
	const { push } = useModal();
	const isDevEnv = isDev();
	const { hasSubaccounts, canDelete } = useDeleteAccount();

	return (
		<div className="flex flex-col gap-y-lg">
			<div className="flex flex-col gap-y-sm">
				<h2 className="typo-label-sm text-text-default">Preferred Explorer</h2>
				<RadioGroup.Default
					size="md"
					variant="card"
					className="grid grid-cols-2 gap-md sm:grid-cols-4"
					value={localSettings.explorer}
					onValueChange={(value) =>
						updateSettings({ ...localSettings, explorer: value })
					}
					items={EXPLORER_OPTIONS}
				/>
			</div>
			<Divider />
			<div className="flex flex-col gap-y-lg">
				<h2 className="typo-label-sm text-text-default">Swap Client</h2>
				<RadioGroup.Default
					size="md"
					variant="card"
					className="grid grid-cols-2 gap-md"
					value={localSettings.swapClientType}
					onValueChange={(value) =>
						updateSettings({ ...localSettings, swapClientType: value })
					}
					items={SWAP_CLIENT_OPTIONS}
				/>
			</div>
			<Divider />
			<SettingsSwitch
				key="versioned_tx_toggle"
				label="Enable Versioned Transactions"
				className="typo-label-sm"
				checked={localSettings.enableVersionedTx}
				onChange={() => toggleSetting('enableVersionedTx')}
				tooltipDefinitionId="settingsVersionedTx"
			/>
			<Divider />
			<div className="flex items-center justify-between">
				<span className="typo-label-sm text-text-default">
					Download Historical Data
				</span>
				<Button
					size="md"
					variant="secondary"
					onClick={() => push({ type: 'export_history', props: {} })}
					disabled={!connected}
					rightIcon={Download}
				>
					Download
				</Button>
			</div>

			{connected && (
				<>
					<Divider />
					<div className="flex flex-col gap-y-sm">
						<div className="flex items-center justify-between">
							<div className="flex flex-col gap-y-xs">
								<span className="typo-label-sm text-text-default">
									Delete Account Data
								</span>
								<span className="typo-body-xs text-text-secondary">
									Permanently delete your off-chain data from Velocity
								</span>
							</div>
							<Tooltip
								disabled={!hasSubaccounts}
								content={'Delete all subaccounts first'}
							>
								<Button
									size="md"
									variant="negative"
									onClick={() => push({ type: 'delete_account', props: {} })}
									disabled={!canDelete}
									rightIcon={Trash}
								>
									Delete
								</Button>
							</Tooltip>
						</div>
					</div>
				</>
			)}

			{isDevEnv && (
				<>
					<Divider />
					<SettingsSwitch
						key="use-devnet"
						label="Use Devnet"
						className="typo-label-sm"
						checked={localSettings.devnetOverride}
						onChange={() => toggleSetting('devnetOverride')}
						dataTestId="settings-use-devnet"
						tooltipContent="This switch makes the client connect to the devnet RPC and use devnet Drift SDK configs."
					/>
					<SettingsSwitch
						label="Use Staging DLOB Server"
						className="typo-label-sm"
						checked={localSettings.stagingDlobServerOverride}
						onChange={() => toggleSetting('stagingDlobServerOverride')}
						tooltipContent="This switch makes the client use the staging DLOB SERVER - should be used with the 'use mainnet' switch being turned on"
					/>
					<SettingsSwitch
						label="Use Staging Data API"
						className="typo-label-sm"
						checked={localSettings.stagingDataServerOverride}
						onChange={() => toggleSetting('stagingDataServerOverride')}
						tooltipContent="This switch makes the client use the staging DATA API - should be used with the 'use mainnet' switch being turned on"
					/>
				</>
			)}
		</div>
	);
};

export default OtherSettings;
